/**
 * Funcionalidades:
 * - Carga resumen de datos (salas y listas)
 * - Muestra KPIs informativos (no clicables)
 * - Muestra últimas listas
 * - Ofrece acciones rápidas
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { 
    Box, 
    Button, 
    Card, 
    CardContent, 
    CircularProgress, 
    Grid, 
    Stack, 
    Typography,
    Avatar,
    Divider,
    useTheme,
    IconButton,
    Paper,
    Tooltip,
    Fade,
    Zoom
} from "@mui/material";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GroupIcon from "@mui/icons-material/Group";
import AddIcon from "@mui/icons-material/Add";
import MapIcon from "@mui/icons-material/Map";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SavingsIcon from "@mui/icons-material/Savings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { motion } from "framer-motion";

import { obtenerListasDelUsuario } from "../api/listas";
import { obtenerSalasDelUsuario } from "../api/salas";
import api from "../api/axios"; // CAMBIO 1: cliente axios para /stats/summary
import DashboardEmptyState from "../components/DashboardEmptyState";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

export default function Dashboard() {
    const navigate = useNavigate();
    const theme = useTheme();

    const userName = localStorage.getItem("user_nombre") || "Usuario";

    const isMobile = window.innerWidth < 900;

    // Estados
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);

    const [listas, setListas] = useState([]);
    const [salas, setSalas] = useState([]);
    const [ahorroEstimado, setAhorroEstimado] = useState(0); // CAMBIO 2: ahorro real desde /stats/summary

    /**
     * Mapa rápido de salas para mostrar nombre
     */
    const salasMap = useMemo(() => {
        const m = new Map();
        (Array.isArray(salas) ? salas : []).forEach((s) => {
            if (s?.idsala != null) m.set(Number(s.idsala), s.nombre);
        });
        return m;
    }, [salas]);

    const formatSalaLabel = (idsala) => {
        if (idsala == null) return "Lista personal";
        const nombre = salasMap.get(Number(idsala));
        return nombre || `Sala #${idsala}`;
    };

    async function cargar() {
        setCargando(true);
        setError(null);

        try {
            // CAMBIO 3: añadida llamada a /stats/summary (si falla, no rompe el dashboard)
            const [listasData, salasData, summaryRes] = await Promise.all([
                obtenerListasDelUsuario(),
                obtenerSalasDelUsuario(),
                api.get("/stats/summary").catch(() => ({ data: null })),
            ]);

            setListas(Array.isArray(listasData) ? listasData : []);
            setSalas(Array.isArray(salasData) ? salasData : []);
            setAhorroEstimado(summaryRes?.data?.ahorro_estimado ?? 0);
        } catch (e) {
            console.error(e);
            setError("No se pudo cargar el resumen de tu actividad.");
        } finally {
            setCargando(false);
        }
    }

    useEffect(() => {
        cargar();
    }, []);

    /**
     * Últimas listas (máx 3)
     */
    const ultimasListas = useMemo(() => {
        return [...listas].slice(0, 3);
    }, [listas]);

    // Loading
    if (cargando) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Error
    if (error) {
        return (
            <Card sx={{ m: 3, borderRadius: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        Ups…
                    </Typography>
                    <Typography sx={{ mb: 2 }}>{error}</Typography>

                    <Button variant="outlined" onClick={cargar}>
                        Reintentar
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Bienvenida y Header */}
            <MotionBox
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{ 
                    mb: 4, 
                    p: 4, 
                    borderRadius: 4, 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(25, 118, 210, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Círculos decorativos */}
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: '20%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                            ¡Hola, {userName}! 👋
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500, mb: 3 }}>
                            Bienvenido a tu centro de ahorro inteligente. ¿Qué quieres hacer hoy?
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button 
                                variant="contained" 
                                color="secondary" 
                                onClick={() => navigate("/compare")}
                                sx={{ 
                                    bgcolor: 'white', 
                                    color: 'primary.main', 
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    px: 3,
                                    textTransform: 'none'
                                }}
                                startIcon={<CompareArrowsIcon />}
                            >
                                Comparar Precios
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
                        <MotionBox 
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            whileTap={{ scale: 0.9 }}
                            sx={{ 
                                position: 'relative',
                                width: 140,
                                height: 140,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '3px solid rgba(255,255,255,0.25)',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                                cursor: 'default'
                            }}
                        >
                            <Box 
                                component={motion.div}
                                animate={{ 
                                    scale: [1, 1.05, 1],
                                    rotate: [0, 5, 0, -5, 0]
                                }}
                                transition={{ 
                                    repeat: Infinity, 
                                    duration: 6,
                                    ease: "easeInOut"
                                }}
                            >
                                <SavingsIcon sx={{ fontSize: '4.5rem', color: 'white', filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))' }} />
                            </Box>
                            
                            {/* Elementos flotantes */}
                            <Box 
                                component={motion.div}
                                animate={{ y: [0, -15, 0], opacity: [0.5, 1, 0.5] }}
                                transition={{ repeat: Infinity, duration: 4, delay: 0 }}
                                sx={{ position: 'absolute', top: 15, right: 20, color: '#ffeb3b' }}
                            >
                                <AutoAwesomeIcon sx={{ fontSize: '1.5rem' }} />
                            </Box>
                            <Box 
                                component={motion.div}
                                animate={{ y: [0, -10, 0], opacity: [0.3, 0.8, 0.3] }}
                                transition={{ repeat: Infinity, duration: 5, delay: 1 }}
                                sx={{ position: 'absolute', bottom: 25, left: 15, color: '#b2ff59' }}
                            >
                                <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
                            </Box>
                        </MotionBox>
                    </Grid>
                </Grid>
            </MotionBox>

            {/* KPIs */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { label: 'SALAS', val: salas.length, icon: <GroupIcon />, color: '#1976d2', delay: 0.1 },
                    { label: 'LISTAS', val: listas.length, icon: <ListAltIcon />, color: '#2e7d32', delay: 0.2 },
                    // CAMBIO 4: ahorro dinámico en lugar de '12%' hardcodeado
                    { label: 'AHORRO', val: `${(ahorroEstimado ?? 0).toFixed(2)}€`, icon: <TrendingUpIcon />, color: '#ed6c02', delay: 0.3 }
                ].map((kpi, idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                        <MotionCard
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: kpi.delay }}
                            whileHover={{ y: -5 }}
                            sx={{ 
                                borderRadius: 4, 
                                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                                background: `linear-gradient(135deg, #fff 0%, ${kpi.color}05 100%)`,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                                            {kpi.label}
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.5 }}>
                                            {kpi.val}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: `${kpi.color}15`, color: kpi.color, width: 56, height: 56 }}>
                                        {kpi.icon}
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </MotionCard>
                    </Grid>
                ))}
            </Grid>

            {/* Acciones y Listas */}
            {listas.length === 0 && salas.length === 0 ? (
                <DashboardEmptyState />
            ) : (
                <Grid container spacing={3}>
                {/* Listas recientes */}
                <Grid item xs={12} md={isMobile ? 12 : 7}>
                    <MotionCard 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                        sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: '0 8px 32px rgba(0,0,0,0.06)' }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ListAltIcon color="primary" /> Listas Recientes
                                </Typography>
                                <Button 
                                    size="small" 
                                    endIcon={<ArrowForwardIcon />} 
                                    onClick={() => navigate("/listas")}
                                    sx={{ fontWeight: 700, textTransform: 'none' }}
                                >
                                    Ver todas
                                </Button>
                            </Stack>

                            {ultimasListas.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 4 }}>
                                    <Typography color="text.secondary">Aún no tienes listas. ¡Crea la primera!</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={2}>
                                    {ultimasListas.map((l) => (
                                        <Paper
                                            key={l.idlista}
                                            elevation={0}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: "background.paper",
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: 'primary.main' + '05',
                                                    transform: 'translateX(4px)'
                                                }
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 800 }}>
                                                    {l.nombre}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatSalaLabel(l.idsala)}
                                                </Typography>
                                            </Box>
                                            <IconButton color="primary" onClick={() => navigate(`/listas/${l.idlista}`)}>
                                                <ArrowForwardIcon />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </MotionCard>
                </Grid>

                {/* Acciones rápidas (Solo en PC) */}
                {!isMobile && (
                    <Grid item xs={12} md={5}>
                    <MotionCard 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 }}
                        sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'divider', boxShadow: '0 8px 32px rgba(0,0,0,0.06)' }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
                                Acciones Rápidas
                            </Typography>

                            <Grid container spacing={2}>
                                {[
                                    { text: "Nueva Lista", icon: <AddIcon />, route: "/listas", color: '#1976d2' },
                                    { text: "Escáner", icon: <CameraAltIcon />, route: "/scan", color: '#2e7d32', mobile: true },
                                    { text: "Ver Mapa", icon: <MapIcon />, route: "/tiendas", color: '#2e7d32', desktop: true },
                                    { text: "Novedades", icon: <NotificationsIcon />, route: "/novedades", color: '#ed6c02', desktop: true },
                                    { text: "Histórico", icon: <TrendingUpIcon />, route: "/historico", color: '#9c27b0', desktop: true }
                                ].filter(a => isMobile ? (a.mobile || !a.desktop) : (a.desktop || !a.mobile)).map((action, idx) => (
                                    <Grid item xs={6} key={idx}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => navigate(action.route)}
                                            sx={{
                                                height: '100px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 4,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                gap: 1,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                color: 'text.primary',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: action.color,
                                                    bgcolor: action.color + '05',
                                                    color: action.color,
                                                    transform: 'scale(1.02)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ 
                                                bgcolor: action.color + '10', 
                                                p: 1, 
                                                borderRadius: 2, 
                                                color: action.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {action.icon}
                                            </Box>
                                            {action.text}
                                        </Button>
                                    </Grid>
                                ))}
                            </Grid>

                            <Divider sx={{ my: 4 }} />

                            <Box sx={{ 
                                p: 2, 
                                borderRadius: 4, 
                                bgcolor: 'primary.main', 
                                color: 'white',
                                textAlign: 'center',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                    💡 Tip del día
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    ¡Compara precios antes de salir! Podrías ahorrar hasta un 20% en tu cesta de la compra.
                                </Typography>
                            </Box>
                        </CardContent>
                    </MotionCard>
                </Grid>
                )}
            </Grid>
            )}
        </Box>
    );
}